import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  getPayslips,
  postPayslipJournal,
  generatePayslip,
  getEmployees,
  clearError,
  clearSuccess,
  addEmployee,
  getDepartments,
  getWorkShifts
} from '../../../../redux/Slices/authSlice';
import { getPositions } from '../../../../redux/Slices/PositionsSlice';
import { getRoles } from '../../../../redux/Slices/RolesSlice';
import {
  FaSearch,
  FaEye,
  FaPlus,
  FaPaperPlane,
  FaPrint,
  FaChevronDown,
  FaTimes,
  FaFilter,
  FaBan,
  FaFileExcel
} from 'react-icons/fa';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { convertArabicNumbersToEnglish, toPositiveInteger, BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';
import axios from 'axios';
import DropdownWithSearch from '../../../../shared/components/DropdownWithSearch';

const Payslips = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const {
    employeeLoans, // imported from selector if needed, but not required
    employees,
    isLoading,
    error,
    success,
    departments,
    positions,
    workShifts
  } = useSelector((state) => state.auth);

  // Core Search & State
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);

  const [rawPayslips, setRawPayslips] = useState([]);
  const [isLoadingPayslips, setIsLoadingPayslips] = useState(false);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null

  // Collapsible Filters
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filters, setFilters] = useState({
    employee_id: '',
    status: '',
    month: '',
    year: ''
  });

  // Employee filter search state
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);
  const employeeDropdownContainerRef = useRef(null);

  // Modal states
  const [showPostJournalModal, setShowPostJournalModal] = useState(false);
  const [payslipToPost, setPayslipToPost] = useState(null);
  const [showGeneratePayslipModal, setShowGeneratePayslipModal] = useState(false);
  const [generatePayslipForm, setGeneratePayslipForm] = useState({
    employee_id: '',
    year: '',
    month: ''
  });

  // Employee dropdown state for generate modal
  const [modalEmployeeDropdownOpen, setModalEmployeeDropdownOpen] = useState(false);
  const [modalEmployeeSearchTerm, setModalEmployeeSearchTerm] = useState('');
  const [modalEmployeeOptions, setModalEmployeeOptions] = useState([]);
  const [modalEmployeePage, setModalEmployeePage] = useState(1);
  const [modalEmployeePagination, setModalEmployeePagination] = useState(null);
  const modalEmployeeSearchInputRef = useRef(null);
  const modalEmployeeDropdownContainerRef = useRef(null);

  // Add employee modal state
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);

  // PDF Preview / Printing
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [pdfPayload, setPdfPayload] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allPayslipsForPdf, setAllPayslipsForPdf] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const pdfContainerRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('sidenav.hr.payslips');
  }, [setTitle]);

  // Fetch all payslips on mount (per_page: 1000 for client filtering)
  const loadAllPayslips = useCallback(async () => {
    setIsLoadingPayslips(true);
    try {
      const result = await dispatch(getPayslips({ page: 1, per_page: 1000, language: i18n.language })).unwrap();
      let list = [];
      if (Array.isArray(result?.data)) {
        list = result.data;
      } else if (Array.isArray(result?.data?.data)) {
        list = result.data.data;
      } else if (Array.isArray(result)) {
        list = result;
      }
      setRawPayslips(list);
    } catch (err) {
      console.error('Error fetching payslips:', err);
    } finally {
      setIsLoadingPayslips(false);
    }
  }, [dispatch, i18n.language]);

  useEffect(() => {
    loadAllPayslips();
  }, [loadAllPayslips]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  // Normalize list responses
  const normalizeListResponse = useCallback((payload) => {
    if (!payload) return { items: [], pagination: null };
    let items = [];
    let pagination = null;
    if (Array.isArray(payload?.data?.data)) {
      items = payload.data.data;
      pagination = payload.data?.meta || payload.data?.pagination || null;
    } else if (Array.isArray(payload?.data)) {
      items = payload.data;
      pagination = payload.meta || payload.pagination || null;
    } else if (Array.isArray(payload?.items)) {
      items = payload.items;
      pagination = payload.pagination || payload.meta || null;
    } else if (Array.isArray(payload)) {
      items = payload;
      pagination = null;
    } else {
      items = [];
      pagination = payload.pagination || payload.meta || null;
    }
    if (pagination) {
      pagination = {
        current_page: pagination.current_page || pagination.page || 1,
        last_page: pagination.last_page || pagination.total_pages || 1,
        per_page: pagination.per_page || 10,
        total: pagination.total || 0,
      };
    }
    return { items, pagination };
  }, []);

  // Fetch employees helper
  const fetchEmployeesHelper = useCallback(async (page, search, setOptions, setPagination) => {
    try {
      const payload = await dispatch(
        getEmployees({
          page,
          per_page: 10,
          search: search || undefined,
          language: i18n.language,
        })
      ).unwrap();
      const { items, pagination: paginationMeta } = normalizeListResponse(payload);
      setOptions(items);
      setPagination(paginationMeta);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, [dispatch, i18n.language, normalizeListResponse]);

  // Fetch employees for Filter Dropdown
  useEffect(() => {
    if (employeeDropdownOpen) {
      fetchEmployeesHelper(employeePage, employeeSearchTerm, setEmployeeOptions, setEmployeePagination);
    }
  }, [employeeDropdownOpen, employeePage, fetchEmployeesHelper, employeeSearchTerm]);

  // Fetch employees for Generate Modal Dropdown
  useEffect(() => {
    if (modalEmployeeDropdownOpen) {
      fetchEmployeesHelper(modalEmployeePage, modalEmployeeSearchTerm, setModalEmployeeOptions, setModalEmployeePagination);
    }
  }, [modalEmployeeDropdownOpen, modalEmployeePage, fetchEmployeesHelper, modalEmployeeSearchTerm]);

  // Debounced search for employee filters
  useEffect(() => {
    if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEmployeePage(1);
        fetchEmployeesHelper(1, employeeSearchTerm, setEmployeeOptions, setEmployeePagination);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [employeeSearchTerm, employeeDropdownOpen, fetchEmployeesHelper]);

  // Debounced search for modal employee dropdown
  useEffect(() => {
    if (modalEmployeeSearchTerm !== undefined && modalEmployeeDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setModalEmployeePage(1);
        fetchEmployeesHelper(1, modalEmployeeSearchTerm, setModalEmployeeOptions, setModalEmployeePagination);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [modalEmployeeSearchTerm, modalEmployeeDropdownOpen, fetchEmployeesHelper]);

  // Fetch extra roles/departments for Add Employee Modal
  useEffect(() => {
    if (isAddEmployeeModalOpen) {
      dispatch(getDepartments());
      dispatch(getPositions());
      dispatch(getWorkShifts());
    }
  }, [isAddEmployeeModalOpen, dispatch]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-dropdown-container]") &&
        !event.target.closest("[data-dropdown-toggle]")
      ) {
        setEmployeeDropdownOpen(false);
        setModalEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Notifications
  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000
        });
        lastSuccessRef.current = { message: success, time: now };
      }

      const isPayslipGeneration = (success.includes('توليد') || success.toLowerCase().includes('generate')) && !success.includes('ترحيل') && !success.toLowerCase().includes('post');
      const isEmployeeAdded = success.includes('موظف') || success.toLowerCase().includes('employee');

      dispatch(clearSuccess());

      if (showGeneratePayslipModal && isPayslipGeneration && !isEmployeeAdded) {
        setShowGeneratePayslipModal(false);
        setGeneratePayslipForm({
          employee_id: '',
          year: '',
          month: ''
        });
      }

      if (isPayslipGeneration) {
        loadAllPayslips();
      }
    }
  }, [success, dispatch, isRTL, showGeneratePayslipModal, loadAllPayslips]);

  useEffect(() => {
    if (error && !isAddEmployeeModalOpen) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL, isAddEmployeeModalOpen]);

  const formatCurrency = (amount) => {
    const formatted = parseFloat(amount).toLocaleString('en-US');
    return convertArabicNumbersToEnglish(formatted) + ' ' + t('payslips.currency');
  };

  const handleApplyPerPage = () => {
    const newPerPage = toPositiveInteger(perPageInput, perPage);
    setPerPage(newPerPage);
    setPerPageInput(newPerPage);
    setCurrentPage(1);
  };

  // --- Client-side Filter / Search / Sort Logic ---
  const filteredPayslips = React.useMemo(() => {
    let data = [...rawPayslips];

    // 1. Search term
    const query = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
    if (query) {
      data = data.filter((item) => {
        const empNameAr = (item.employee?.name_ar || '').toLowerCase();
        const empNameEn = (item.employee?.name_en || '').toLowerCase();
        const empName = (item.employee?.name || '').toLowerCase();
        const empCode = (item.employee?.employee_code || '').toLowerCase();
        const grossSalary = item.gross_salary ? String(item.gross_salary) : '';
        const netSalary = item.net_salary ? String(item.net_salary) : '';
        const period = (item.pay_period || '').toLowerCase();
        const status = (item.status || '').toLowerCase();
        const statusTrans = t(`payslips.status.${item.status}`, { defaultValue: item.status || '' }).toLowerCase();

        return empNameAr.includes(query) ||
               empNameEn.includes(query) ||
               empName.includes(query) ||
               empCode.includes(query) ||
               grossSalary.includes(query) ||
               netSalary.includes(query) ||
               period.includes(query) ||
               status.includes(query) ||
               statusTrans.includes(query);
      });
    }

    // 2. Employee filter
    if (filters.employee_id) {
      data = data.filter(item => String(item.employee?.id) === String(filters.employee_id));
    }

    // 3. Status filter
    if (filters.status) {
      data = data.filter(item => item.status === filters.status);
    }

    // 4. Month filter
    if (filters.month) {
      data = data.filter(item => {
        if (item.month !== undefined && item.month !== null) {
          return String(item.month) === String(filters.month);
        }
        if (item.pay_period) {
          const parts = item.pay_period.split('-');
          if (parts[1]) {
            return String(parseInt(parts[1])) === String(parseInt(filters.month));
          }
        }
        return true;
      });
    }

    // 5. Year filter
    if (filters.year) {
      data = data.filter(item => {
        if (item.year !== undefined && item.year !== null) {
          return String(item.year) === String(filters.year);
        }
        if (item.pay_period) {
          const parts = item.pay_period.split('-');
          if (parts[0]) {
            return String(parts[0]) === String(filters.year);
          }
        }
        return true;
      });
    }

    // 6. Sorting
    if (sortField && sortOrder) {
      data.sort((a, b) => {
        let aVal = '';
        let bVal = '';

        if (sortField === 'employee') {
          aVal = isRTL ? (a.employee?.name_ar || a.employee?.name || '') : (a.employee?.name_en || a.employee?.name || '');
          bVal = isRTL ? (b.employee?.name_ar || b.employee?.name || '') : (b.employee?.name_en || b.employee?.name || '');
        } else if (sortField === 'period') {
          aVal = a.pay_period || '';
          bVal = b.pay_period || '';
        } else if (sortField === 'gross_salary') {
          const aNum = Number(a.gross_salary) || 0;
          const bNum = Number(b.gross_salary) || 0;
          return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        } else if (sortField === 'net_salary') {
          const aNum = Number(a.net_salary) || 0;
          const bNum = Number(b.net_salary) || 0;
          return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        } else if (sortField === 'status') {
          aVal = t(`payslips.status.${a.status}`, { defaultValue: a.status || '' });
          bVal = t(`payslips.status.${b.status}`, { defaultValue: b.status || '' });
        }

        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal, isRTL ? 'ar' : 'en', { sensitivity: 'base' })
          : bVal.localeCompare(aVal, isRTL ? 'ar' : 'en', { sensitivity: 'base' });
      });
    }

    return data;
  }, [rawPayslips, searchTerm, filters, sortField, sortOrder, isRTL, t]);

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredPayslips.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedPayslips = filteredPayslips.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ employee_id: '', status: '', month: '', year: '' });
    setSearchTerm('');
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  };

  // 3-state sorting toggle
  const handleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortOrder('asc');
    } else if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else if (sortOrder === 'desc') {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ color: 'white', marginInlineStart: '6px', fontSize: '12px' }}>⇅</span>;
    if (sortOrder === 'asc') return <span style={{ color: 'white', marginInlineStart: '6px', fontSize: '12px' }}>▲</span>;
    return <span style={{ color: 'white', marginInlineStart: '6px', fontSize: '12px' }}>▼</span>;
  };

  const handlePostJournalClick = (payslip) => {
    setPayslipToPost(payslip);
    setShowPostJournalModal(true);
  };

  const confirmPostJournal = async () => {
    if (payslipToPost) {
      const result = await dispatch(postPayslipJournal(payslipToPost.id));
      if (postPayslipJournal.fulfilled.match(result)) {
        setShowPostJournalModal(false);
        setPayslipToPost(null);
        loadAllPayslips();
      }
    }
  };

  const handleGeneratePayslipFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'year') {
      const [year, month] = value.split('-');
      setGeneratePayslipForm(prev => ({
        ...prev,
        year: year || '',
        month: month || ''
      }));
    } else {
      setGeneratePayslipForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleGeneratePayslipSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!generatePayslipForm.employee_id) {
      toast.error(t('payslips.generateModal.errors.selectEmployee', { defaultValue: 'يرجى اختيار الموظف' }), { rtl: isRTL });
      return;
    }
    if (!generatePayslipForm.year) {
      toast.error(t('payslips.generateModal.errors.enterYear', { defaultValue: 'يرجى إدخال السنة' }), { rtl: isRTL });
      return;
    }

    const payslipData = {
      employee_id: parseInt(generatePayslipForm.employee_id),
      year: parseInt(generatePayslipForm.year),
      month: parseInt(generatePayslipForm.month)
    };

    dispatch(generatePayslip(payslipData));
  };

  const formatPrintCurrency = (value) => {
    const formatted = Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return convertArabicNumbersToEnglish(formatted) + ' ' + t('payslips.currency');
  };

  // --- Dynamic PDF Pagination ---
  const colStyles = {
    employee: { width: '35%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
    period: { width: '20%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    grossSalary: { width: '15%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    netSalary: { width: '15%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    status: { width: '15%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' }
  };

  const calculateDynamicChunks = useCallback((list) => {
    const container = document.getElementById('payslips-measurement-container');
    if (!container) return [list];
    const tbody = container.querySelector('tbody');
    if (!tbody) return [list];

    const maxHeight = 700; // px height constraint
    const chunks = [];
    let currentChunk = [];
    let currentHeight = 0;

    const rows = tbody.querySelectorAll('tr');
    for (let i = 0; i < rows.length && i < list.length; i++) {
      const rowHeight = rows[i].offsetHeight || 25;
      if (currentHeight + rowHeight > maxHeight && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [list[i]];
        currentHeight = rowHeight;
      } else {
        currentChunk.push(list[i]);
        currentHeight += rowHeight;
      }
    }
    if (currentChunk.length > 0) chunks.push(currentChunk);
    return chunks.length > 0 ? chunks : [list];
  }, []);

  const renderPdfPage = (chunk, chunkIdx, totalChunks, elementId = null) => {
    const hasActiveFilters = searchTerm.trim() || filters.employee_id || filters.status || filters.month || filters.year;

    return (
      <div
        id={elementId}
        style={{ width: '794px', height: '1123px', backgroundColor: '#ffffff', color: '#000000', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold', padding: '40px', boxSizing: 'border-box', overflow: 'hidden' }}
      >
        <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
          <div style={{ border: '2px solid #000', height: '1033px', padding: '18px 18px 10px', position: 'relative', display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '60%', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat', opacity: 0.05, zIndex: 0, pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', display: 'block', flexShrink: 0 }} crossOrigin="anonymous" />
              <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير كشوف المرتبات' : 'Payslips Report'}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                  {isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIdx + 1} / {totalChunks}
                </div>
                {hasActiveFilters && (
                  <div style={{ fontSize: '9px', color: '#444', marginTop: '6px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', backgroundColor: 'rgba(238,242,247,0.5)', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                    {searchTerm.trim() && <span>{isRTL ? 'بحث' : 'Search'}: {searchTerm.trim()}</span>}
                    {filters.employee_id && <span>{t('payslips.table.employee')}: {(isRTL ? employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name_ar : employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name_en) || employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name || filters.employee_id}</span>}
                    {filters.status && <span>{t('payslips.table.status')}: {t(`payslips.status.${filters.status}`)}</span>}
                    {filters.month && <span>{isRTL ? 'الشهر' : 'Month'}: {filters.month}</span>}
                    {filters.year && <span>{isRTL ? 'السنة' : 'Year'}: {filters.year}</span>}
                  </div>
                )}
              </div>
              <div style={{ marginTop: '10px', direction: isRTL ? 'rtl' : 'ltr', flex: 1, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", serif', backgroundColor: 'rgba(255,255,255,0.7)', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '32%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '12%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: '#e3f2fd', color: '#0d2a50' }}>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>#</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: isRTL ? 'right' : 'left' }}>{t('payslips.table.employee')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('payslips.table.payPeriod')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('payslips.table.grossSalary')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('payslips.table.netSalary')}</th>
                      <th style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '11px', textAlign: 'center' }}>{t('payslips.table.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((item, index) => {
                      const globalIdx = allPayslipsForPdf.indexOf(item) + 1;
                      return (
                        <tr key={item.id}>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000' }}>{globalIdx}</td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: isRTL ? 'right' : 'left', color: '#000', wordBreak: 'break-all', whiteSpace: 'normal' }}>
                            <div>{(isRTL ? item.employee?.name_ar : item.employee?.name_en) || item.employee?.name || '—'}</div>
                            <div style={{ fontSize: '8px', color: '#666' }}>{item.employee?.employee_code || ''}</div>
                          </td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000' }}>{item.pay_period || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000', fontWeight: 'bold' }}>{item.gross_salary ? parseFloat(item.gross_salary).toLocaleString('en-US') : '0'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000', fontWeight: 'bold' }}>{item.net_salary ? parseFloat(item.net_salary).toLocaleString('en-US') : '0'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px 2px', fontSize: '10px', textAlign: 'center', color: '#000' }}>
                            {item.status === 'generated'
                              ? t('payslips.status.generated')
                              : item.status === 'posted'
                                ? t('payslips.status.posted', { defaultValue: 'مرحل' })
                                : item.status === 'paid'
                                  ? t('payslips.status.paid')
                                  : item.status || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px', display: 'block' }} crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSinglePdfPage = (payslip, isPreview = false) => {
    return (
      <div
        id={isPreview ? undefined : `employee-payslip-pdf-single-${payslip.id}`}
        style={{ width: '794px', height: '1123px', backgroundColor: '#ffffff', color: '#001437', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', padding: '24px', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr', overflow: 'hidden' }}
      >
        <div style={{ border: '2px solid #000', height: '1043px', padding: '18px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }} crossOrigin="anonymous" />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>{t('payslips.print.labels.employeePayslip', { defaultValue: isRTL ? 'كشف راتب موظف' : 'Employee Payslip' })}</h2>
                <div style={{ fontSize: '14px', marginTop: '4px', direction: dir }}>{payslip.pay_period || ''}</div>
              </div>
              <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                  <div style={{ wordBreak: 'break-all', maxWidth: '200px', whiteSpace: 'normal' }}><strong>{t('payslips.print.labels.name', { defaultValue: isRTL ? 'الاسم' : 'Name' })}:</strong> {(isRTL ? payslip.employee?.name_ar : payslip.employee?.name_en) || payslip.employee?.name || '—'}</div>
                  <div><strong>{t('payslips.print.labels.code', { defaultValue: isRTL ? 'الكود' : 'Code' })}:</strong> {payslip.employee?.employee_code || '—'}</div>
                  <div style={{ wordBreak: 'break-all', maxWidth: '200px', whiteSpace: 'normal' }}><strong>{t('payslips.print.labels.email', { defaultValue: isRTL ? 'البريد' : 'Email' })}:</strong> {payslip.employee?.email || '—'}</div>
                  <div style={{ wordBreak: 'break-all', maxWidth: '200px', whiteSpace: 'normal' }}><strong>{t('payslips.print.labels.phone', { defaultValue: isRTL ? 'الهاتف' : 'Phone' })}:</strong> {payslip.employee?.phone || '—'}</div>
                </div>
              </div>
              <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', fontSize: '13px', lineHeight: 1.6, direction: dir, textAlign }}>
                  <div><strong>{t('payslips.print.labels.payPeriod', { defaultValue: isRTL ? 'الفترة' : 'Pay Period' })}:</strong> {payslip.pay_period || '—'}</div>
                  <div><strong>{t('payslips.print.labels.grossSalary', { defaultValue: isRTL ? 'الراتب الإجمالي' : 'Gross Salary' })}:</strong> {formatPrintCurrency(payslip.gross_salary)}</div>
                  <div><strong>{t('payslips.print.labels.netSalary', { defaultValue: isRTL ? 'الراتب الصافي' : 'Net Salary' })}:</strong> {formatPrintCurrency(payslip.net_salary)}</div>
                  <div><strong>{t('payslips.print.labels.status', { defaultValue: isRTL ? 'الحالة' : 'Status' })}:</strong> {payslip.status === 'generated' ? t('payslips.status.generated', { defaultValue: isRTL ? 'تم التوليد' : 'Generated' }) : payslip.status === 'posted' ? t('payslips.status.posted', { defaultValue: isRTL ? 'مرحل' : 'Posted' }) : payslip.status || '—'}</div>
                </div>
              </div>
              {payslip.breakdown && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#0d2a50', marginBottom: '8px', direction: dir, textAlign }}>{t('payslips.print.labels.salaryDetails', { defaultValue: isRTL ? 'تفاصيل الراتب' : 'Salary Details' })}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', direction: dir }}>
                    <thead>
                      <tr style={{ backgroundColor: '#e3f2fd', color: '#0d2a50' }}>
                        <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.item', { defaultValue: isRTL ? 'البند' : 'Item' })}</th>
                        <th style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.amount', { defaultValue: isRTL ? 'المبلغ' : 'Amount' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payslip.breakdown.base_salary && (
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.baseSalary', { defaultValue: isRTL ? 'الراتب الأساسي' : 'Base Salary' })}</td>
                          <td style={{ border: '1px solid #000', padding: '8px', fontWeight: '700', textAlign }}>{formatPrintCurrency(payslip.breakdown.base_salary)}</td>
                        </tr>
                      )}
                      {payslip.breakdown.allowances && Object.keys(payslip.breakdown.allowances).map((key) => (
                        <tr key={key}>
                          <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.allowance', { defaultValue: isRTL ? 'بدل' : 'Allowance' })} {key}</td>
                          <td style={{ border: '1px solid #000', padding: '8px', color: '#1976d2', fontWeight: '700', textAlign }}>+{formatPrintCurrency(payslip.breakdown.allowances[key])}</td>
                        </tr>
                      ))}
                      {payslip.breakdown.fixed_deductions && Array.isArray(payslip.breakdown.fixed_deductions) && payslip.breakdown.fixed_deductions.map((deduction, idx) => (
                        <tr key={`deduction-${idx}`}>
                          <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{deduction.name || t('payslips.print.labels.deduction', { defaultValue: isRTL ? 'خصم' : 'Deduction' })}</td>
                          <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(deduction.amount || deduction)}</td>
                        </tr>
                      ))}
                      {payslip.breakdown.calculation_details?.absence_deduction && (
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '8px', textAlign }}>{t('payslips.print.labels.absenceDeduction', { defaultValue: isRTL ? 'خصم الغياب' : 'Absence Deduction' })}</td>
                          <td style={{ border: '1px solid #000', padding: '8px', color: '#c0392b', fontWeight: '700', textAlign }}>-{formatPrintCurrency(payslip.breakdown.calculation_details.absence_deduction)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {payslip.breakdown?.calculation_details && (
                <div style={{ border: '1px solid #000', padding: '12px', fontSize: '12px', lineHeight: 1.7, direction: dir, textAlign }}>
                  <div style={{ fontWeight: '700', marginBottom: '8px', color: '#0d2a50' }}>{t('payslips.print.labels.calculationDetails', { defaultValue: isRTL ? 'تفاصيل الحساب' : 'Calculation Details' })}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                    {payslip.breakdown.calculation_details.total_work_days !== undefined && (
                      <div><strong>{t('payslips.print.labels.totalWorkDays', { defaultValue: isRTL ? 'أيام العمل' : 'Total Work Days' })}:</strong> {payslip.breakdown.calculation_details.total_work_days}</div>
                    )}
                    {payslip.breakdown.calculation_details.attended_days !== undefined && (
                      <div><strong>{t('payslips.print.labels.attendedDays', { defaultValue: isRTL ? 'أيام الحضور' : 'Attended Days' })}:</strong> {payslip.breakdown.calculation_details.attended_days}</div>
                    )}
                    {payslip.breakdown.calculation_details.leave_days !== undefined && (
                      <div><strong>{t('payslips.print.labels.leaveDays', { defaultValue: isRTL ? 'أيام الإجازة' : 'Leave Days' })}:</strong> {payslip.breakdown.calculation_details.leave_days}</div>
                    )}
                    {payslip.breakdown.calculation_details.absent_days !== undefined && (
                      <div><strong>{t('payslips.print.labels.absentDays', { defaultValue: isRTL ? 'أيام الغياب' : 'Absent Days' })}:</strong> {payslip.breakdown.calculation_details.absent_days}</div>
                    )}
                    {payslip.breakdown.calculation_details.daily_rate !== undefined && (
                      <div><strong>{t('payslips.print.labels.dailyRate', { defaultValue: isRTL ? 'المعدل اليومي' : 'Daily Rate' })}:</strong> {formatPrintCurrency(payslip.breakdown.calculation_details.daily_rate)}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
              <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px', flexShrink: 0 }} crossOrigin="anonymous" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handlePreviewSingle = (payslip) => {
    if (!payslip) return;
    setPdfPayload(payslip);
    setPdfType('statement');
    setPreviewData({ type: 'single', data: payslip });
    setIsPreviewOpen(true);
  };

  const handlePreviewAll = () => {
    const list = filteredPayslips;
    if (list.length === 0) {
      toast.error(t('payslips.preview.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    setAllPayslipsForPdf(list);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const chunks = calculateDynamicChunks(list);
        setPdfChunks(chunks);
        setPdfType('all');
        setPreviewData({ type: 'all', data: list, chunks });
        setIsPreviewOpen(true);
      }, 200);
    });
  };

  const handlePrintFromPreview = async () => {
    if (!previewData) return;
    setIsPreviewOpen(false);
    if (previewData.type === 'single') {
      await handlePrintSingle(previewData.data);
    } else {
      await handlePrintAll();
    }
  };

  const handlePrintSingle = async (payslip) => {
    setPdfPayload(payslip);
    setPdfType('statement');
    await new Promise((resolve) => setTimeout(resolve, 300));
    setIsGeneratingPdf(true);
    try {
      const element = document.getElementById(`employee-payslip-pdf-single-${payslip.id}`);
      if (!element) throw new Error("Template not found");
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/png", 1.0), "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      pdf.save(`Payslip_${payslip.employee?.employee_code || payslip.employee?.name || payslip.id}_${payslip.pay_period || ''}.pdf`);
      toast.success(t('payslips.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (err) {
      console.error(err);
      toast.error(t('payslips.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف كشف الرواتب' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPayload(null);
    }
  };

  const handlePrintAll = async () => {
    const list = filteredPayslips;
    if (!list || list.length === 0) {
      toast.error(t('payslips.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }
    setAllPayslipsForPdf(list);

    await new Promise((resolve) => setTimeout(resolve, 300));
    const chunks = calculateDynamicChunks(list);
    setPdfChunks(chunks);
    setPdfType("all");
    await new Promise((resolve) => setTimeout(resolve, 600));
    setIsGeneratingPdf(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();

      for (let i = 0; i < chunks.length; i++) {
        const element = document.getElementById(`payslips-pdf-all-${i}`);
        if (!element) continue;
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      }
      pdf.save(`ACE_Payslips_All_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('payslips.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (err) {
      console.error(err);
      toast.error(t('payslips.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف كشف الرواتب' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
      setAllPayslipsForPdf([]);
    }
  };

  // --- Excel Export (current filtered) ---
  const handleExportToExcel = () => {
    try {
      const list = filteredPayslips;
      if (list.length === 0) {
        toast.error(t('payslips.noDataToExport', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
        return;
      }

      const exportData = list.map((payslip, index) => ({
        [t('common.serialNumber', { defaultValue: '#' })]: index + 1,
        [t('payslips.table.employee', { defaultValue: 'الموظف' })]:
          isRTL
            ? payslip.employee?.name_ar || payslip.employee?.name || '—'
            : payslip.employee?.name_en || payslip.employee?.name || '—',
        [t('payslips.table.code', { defaultValue: 'الكود' })]: payslip.employee?.employee_code || '—',
        [t('payslips.table.payPeriod', { defaultValue: 'الفترة' })]: payslip.pay_period || '—',
        [t('payslips.table.grossSalary', { defaultValue: 'الراتب الإجمالي' })]: payslip.gross_salary || 0,
        [t('payslips.table.netSalary', { defaultValue: 'الراتب الصافي' })]: payslip.net_salary || 0,
        [t('payslips.table.status', { defaultValue: 'الحالة' })]:
          payslip.status === 'generated'
            ? t('payslips.status.generated')
            : payslip.status === 'posted'
              ? t('payslips.status.posted', { defaultValue: 'مرحل' })
              : payslip.status === 'paid'
                ? t('payslips.status.paid')
                : payslip.status || '—'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('payslips.excel.sheetName', { defaultValue: 'Payslips' }));
      const fileName = `${t('payslips.excel.fileName', { defaultValue: 'Payslips' })}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('payslips.exportSuccess', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (err) {
      console.error('Error exporting payslips:', err);
      toast.error(t('payslips.exportError', { defaultValue: 'حدث خطأ أثناء التصدير' }), { rtl: isRTL });
    }
  };

  // Single statement pdf data source
  const statementPrintData = pdfType === 'statement' ? pdfPayload : null;

  return (
    <>
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
        {/* Header Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '20px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', margin: 0, fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
            {t('payslips.title')}
          </h1>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowGeneratePayslipModal(true)}
              style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}
            >
              <FaPlus />
              {t('payslips.generateButton')}
            </button>
            <button
              onClick={handlePreviewAll}
              disabled={filteredPayslips.length === 0}
              style={{ backgroundColor: filteredPayslips.length === 0 ? '#666' : '#17a2b8', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: filteredPayslips.length === 0 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}
            >
              <FaEye />
              {t('payslips.previewAll', { defaultValue: 'معاينة PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf || filteredPayslips.length === 0}
              style={{ backgroundColor: isGeneratingPdf || filteredPayslips.length === 0 ? '#666' : '#6c757d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: isGeneratingPdf || filteredPayslips.length === 0 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}
            >
              <FaPrint />
              {t('payslips.printAll', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportToExcel}
              disabled={filteredPayslips.length === 0}
              style={{ backgroundColor: filteredPayslips.length === 0 ? '#666' : '#28a745', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: filteredPayslips.length === 0 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}
            >
              <FaFileExcel />
              {t('payslips.exportAll', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        </div>

        {/* Search & Collapsible Filters Toggle */}
        <div style={{ backgroundColor: '#1a1f2e', padding: '20px', borderRadius: '12px', border: '1px solid #333', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '280px', maxWidth: '500px' }}>
              <FaSearch style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '15px', top: '50%', transform: 'translateY(-50%)', color: '#666', fontSize: '16px', zIndex: 1, pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder={t('payslips.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: isRTL ? '12px 45px 12px 15px' : '12px 15px 12px 45px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <button
              onClick={() => setIsFilterOpen((o) => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: isFilterOpen ? '#AC2000' : '#2d3748', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}
            >
              <FaFilter /> {isRTL ? 'الفلاتر' : 'Filters'}
            </button>
          </div>

          {/* Filters Panel */}
          {isFilterOpen && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #333' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                
                {/* Employee Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('payslips.table.employee')}
                  </label>
                  <div style={{ position: 'relative' }} data-dropdown-container="true">
                    <div
                      data-dropdown-toggle="true"
                      onClick={() => setEmployeeDropdownOpen((o) => !o)}
                      style={{ width: '100%', padding: '12px 15px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '8px', color: filters.employee_id ? 'white' : '#666', fontSize: '14px', minHeight: '45px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box' }}
                    >
                      <span>
                        {filters.employee_id
                          ? employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name || (isRTL ? 'موظف محدد' : 'Selected')
                          : (isRTL ? 'جميع الموظفين' : 'All Employees')}
                      </span>
                      {filters.employee_id ? (
                        <span onClick={(e) => { e.stopPropagation(); handleFilterChange('employee_id', ''); setEmployeeDropdownOpen(false); }} style={{ cursor: 'pointer', color: '#dc3545', fontSize: '12px', padding: '2px 4px' }}>✕</span>
                      ) : (
                        <span style={{ fontSize: 12 }}>▼</span>
                      )}
                    </div>
                    {employeeDropdownOpen && (
                      <DropdownWithSearch
                        isOpen={employeeDropdownOpen}
                        searchTerm={employeeSearchTerm}
                        onSearchChange={setEmployeeSearchTerm}
                        searchInputRef={employeeSearchInputRef}
                        items={employeeOptions}
                        pagination={employeePagination}
                        currentPage={employeePage}
                        onPageChange={setEmployeePage}
                        onClose={() => setEmployeeDropdownOpen(false)}
                        onSelect={(id) => {
                          handleFilterChange('employee_id', String(id));
                          setEmployeeDropdownOpen(false);
                          setEmployeeSearchTerm('');
                          setEmployeePage(1);
                        }}
                        label={isRTL ? 'الموظف' : 'Employee'}
                        isRTL={isRTL}
                        t={t}
                        searchPlaceholder={isRTL ? 'ابحث عن موظف...' : 'Search for employee...'}
                      />
                    )}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('payslips.table.status')}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', minHeight: '45px', outline: 'none', direction: dir }}
                  >
                    <option value="">{isRTL ? 'الكل' : 'All'}</option>
                    <option value="generated">{t('payslips.status.generated')}</option>
                    <option value="posted">{t('payslips.status.posted', { defaultValue: 'مرحل' })}</option>
                    <option value="paid">{t('payslips.status.paid')}</option>
                  </select>
                </div>

                {/* Month Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500', textAlign: isRTL ? 'right' : 'left' }}>
                    {isRTL ? 'الشهر' : 'Month'}
                  </label>
                  <select
                    value={filters.month}
                    onChange={(e) => handleFilterChange('month', e.target.value)}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', minHeight: '45px', outline: 'none', direction: dir }}
                  >
                    <option value="">{isRTL ? 'الكل' : 'All'}</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={String(m)}>{m}</option>
                    ))}
                  </select>
                </div>

                {/* Year Filter */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#ccc', fontSize: '14px', fontWeight: '500', textAlign: isRTL ? 'right' : 'left' }}>
                    {isRTL ? 'السنة' : 'Year'}
                  </label>
                  <select
                    value={filters.year}
                    onChange={(e) => handleFilterChange('year', e.target.value)}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', minHeight: '45px', outline: 'none', direction: dir }}
                  >
                    <option value="">{isRTL ? 'الكل' : 'All'}</option>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Filter Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', borderTop: '1px solid #333', paddingTop: '15px' }}>
                <button onClick={clearFilters} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                  {isRTL ? 'إعادة ضبط' : 'Reset'}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '14px', color: '#ccc', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                    {t('payslips.perPage', { defaultValue: 'عدد السجلات' })}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '4px' }}>
                    <button onClick={() => setPerPageInput(Math.max(1, toPositiveInteger(perPageInput, perPage) - 1))} style={{ padding: '8px 12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px' }}>-</button>
                    <input type="number" value={perPageInput} onChange={(e) => setPerPageInput(toPositiveInteger(e.target.value, perPage))} style={{ width: '50px', background: 'none', border: 'none', color: 'white', textAlign: 'center', appearance: 'textfield', margin: 0, fontWeight: 'bold' }} />
                    <button onClick={() => setPerPageInput(toPositiveInteger(perPageInput, perPage) + 1)} style={{ padding: '8px 12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px' }}>+</button>
                  </div>
                  <button onClick={handleApplyPerPage} style={{ padding: '10px 20px', backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                    {t('common.apply', { defaultValue: 'تطبيق' })}
                  </button>
                </div>
              </div>

              {/* Total Filtered Count */}
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#aaa', textAlign: isRTL ? 'right' : 'left' }}>
                {isRTL
                  ? `يعرض ${filteredPayslips.length} من أصل ${rawPayslips.length} سجل`
                  : `Showing ${filteredPayslips.length} of ${rawPayslips.length} records`}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', border: '1px solid #333', overflow: 'hidden' }}>
          {paginatedPayslips.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign }}>
                <thead>
                  <tr style={{ backgroundColor: '#1a1f2e', borderBottom: '2px solid #AC2000' }}>
                    <th style={{ padding: '15px', color: 'white', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', textAlign: 'center' }}>
                      {t('payslips.table.number')}
                    </th>
                    <th onClick={() => handleSort('employee')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', textAlign: 'center', cursor: 'pointer' }}>
                      {t('payslips.table.employee')} {getSortIcon('employee')}
                    </th>
                    <th onClick={() => handleSort('period')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', textAlign: 'center', cursor: 'pointer' }}>
                      {t('payslips.table.payPeriod')} {getSortIcon('period')}
                    </th>
                    <th onClick={() => handleSort('gross_salary')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', textAlign: 'center', cursor: 'pointer' }}>
                      {t('payslips.table.grossSalary')} {getSortIcon('gross_salary')}
                    </th>
                    <th onClick={() => handleSort('net_salary')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', textAlign: 'center', cursor: 'pointer' }}>
                      {t('payslips.table.netSalary')} {getSortIcon('net_salary')}
                    </th>
                    <th onClick={() => handleSort('status')} style={{ padding: '15px', color: 'white', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', textAlign: 'center', cursor: 'pointer' }}>
                      {t('payslips.table.status')} {getSortIcon('status')}
                    </th>
                    <th style={{ padding: '15px', color: 'white', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', textAlign: 'center' }}>
                      {t('payslips.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPayslips.map((payslip, index) => {
                    const recordIndex = filteredPayslips.length - ((currentPageSafe - 1) * perPage + index);
                    return (
                      <tr key={payslip.id} style={{ borderBottom: '2px solid #444', borderTop: index === 0 ? '2px solid #444' : 'none' }}>
                        <td style={{ padding: '15px', textAlign: 'center', color: 'white' }}>{recordIndex}</td>
                        <td style={{ padding: '15px', textAlign: 'center', color: 'white' }}>
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{(isRTL ? payslip.employee?.name_ar : payslip.employee?.name_en) || payslip.employee?.name || t('payslips.notAvailable')}</div>
                            <div style={{ fontSize: '12px', color: '#ccc' }}>{payslip.employee?.employee_code || '-'}</div>
                          </div>
                        </td>
                        <td style={{ padding: '15px', textAlign: 'center', color: 'white' }}>{payslip.pay_period || t('payslips.notAvailable')}</td>
                        <td style={{ padding: '15px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>{payslip.gross_salary ? formatCurrency(payslip.gross_salary) : t('payslips.notAvailable')}</td>
                        <td style={{ padding: '15px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>{payslip.net_salary ? formatCurrency(payslip.net_salary) : t('payslips.notAvailable')}</td>
                        <td style={{ padding: '15px', textAlign: 'center', color: 'white' }}>
                          <span style={{
                            padding: '5px 15px',
                            borderRadius: '20px',
                            backgroundColor: payslip.status === 'generated'
                              ? 'rgba(172, 32, 0, 0.4)'
                              : payslip.status === 'paid'
                                ? 'rgba(0, 123, 255, 0.4)'
                                : 'rgba(40, 167, 69, 0.4)',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {payslip.status === 'generated'
                              ? t('payslips.status.generated')
                              : payslip.status === 'posted'
                                ? t('payslips.status.posted', { defaultValue: 'مرحل' })
                                : payslip.status === 'paid'
                                  ? t('payslips.status.paid')
                                  : payslip.status || t('payslips.status.notAvailable')}
                          </span>
                        </td>
                        <td style={{ padding: '15px' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handlePreviewSingle(payslip)}
                              style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                              title={t('payslips.actions.view', { defaultValue: 'View Details' })}
                            >
                              <FaEye />
                            </button>
                            <button
                              onClick={() => handlePrintSingle(payslip)}
                              disabled={isGeneratingPdf}
                              style={{ backgroundColor: '#1f5ff1', color: 'white', border: 'none', padding: '8px', borderRadius: '6px', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: isGeneratingPdf ? 0.7 : 1 }}
                              title={t('payslips.actions.print', { defaultValue: 'Print' })}
                            >
                              <FaPrint />
                            </button>
                            {payslip.status === 'generated' && payslip.status !== 'posted' && (
                              <button
                                onClick={() => handlePostJournalClick(payslip)}
                                style={{ backgroundColor: '#17a2b8', color: 'white', border: 'none', padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                                title={t('payslips.actions.postJournal', { defaultValue: 'Post Journal' })}
                              >
                                <FaPaperPlane />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {t('payslips.noData')}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16, borderTop: '1px solid #333' }}>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPageSafe === 1}
                style={{ padding: '8px 16px', backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000', color: 'white', border: 'none', borderRadius: 6, cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer', opacity: currentPageSafe === 1 ? 0.6 : 1 }}
              >
                {t('payslips.pagination.previous', { defaultValue: 'السابق' })}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || (p >= currentPageSafe - 2 && p <= currentPageSafe + 2))
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && p - arr[idx - 1] > 1 && <span style={{ color: '#666', padding: '8px 4px' }}>...</span>}
                    <button
                      onClick={() => setCurrentPage(p)}
                      style={{ padding: '8px 12px', backgroundColor: p === currentPageSafe ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 4, cursor: 'pointer' }}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPageSafe === totalPages}
                style={{ padding: '8px 16px', backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000', color: 'white', border: 'none', borderRadius: 6, cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer', opacity: currentPageSafe === totalPages ? 0.6 : 1 }}
              >
                {t('payslips.pagination.next', { defaultValue: 'التالي' })}
              </button>
            </div>
          )}
        </div>

        {/* Modal: Confirm Post Journal */}
        {showPostJournalModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
                <div className="modal-header" style={{ border: 'none', borderBottom: '1px solid #333' }}>
                  <h5 className="modal-title text-white" style={{ textAlign }}>
                    {t('payslips.postJournal.title', { defaultValue: 'تأكيد ترحيل الكشف' })}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => { setShowPostJournalModal(false); setPayslipToPost(null); }}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body text-white" style={{ textAlign }}>
                  <p style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', marginBottom: '10px' }}>
                    {t('payslips.postJournal.confirm', { defaultValue: 'هل أنت متأكد من ترحيل كشف الراتب؟' })}
                  </p>
                  {payslipToPost && (
                    <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#1a1f2e', borderRadius: '8px' }}>
                      <p style={{ margin: '5px 0', fontSize: '14px', direction: dir, textAlign }}>
                        <strong>{t('payslips.postJournal.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })}:</strong> {(isRTL ? payslipToPost.employee?.name_ar : payslipToPost.employee?.name_en) || payslipToPost.employee?.name || '-'}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', direction: dir, textAlign }}>
                        <strong>{t('payslips.postJournal.period', { defaultValue: isRTL ? 'الفترة' : 'Period' })}:</strong> {payslipToPost.pay_period || '-'}
                      </p>
                    </div>
                  )}
                </div>
                <div className="modal-footer" style={{ border: 'none' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowPostJournalModal(false); setPayslipToPost(null); }} style={{ backgroundColor: '#6c757d', border: 'none', borderRadius: '6px', padding: '8px 20px' }}>
                    {t('payslips.postJournal.cancel', { defaultValue: isRTL ? 'إلغاء' : 'Cancel' })}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmPostJournal} disabled={isLoading} style={{ backgroundColor: '#17a2b8', border: 'none', borderRadius: '6px', padding: '8px 20px', opacity: isLoading ? 0.6 : 1 }}>
                    {isLoading ? t('payslips.postJournal.posting', { defaultValue: isRTL ? 'جاري الترحيل...' : 'Posting...' }) : t('payslips.postJournal.confirmButton', { defaultValue: isRTL ? 'ترحيل' : 'Post' })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Generate Payslip */}
        {showGeneratePayslipModal && ReactDOM.createPortal(
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999999, padding: '20px' }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setShowGeneratePayslipModal(false);
                setGeneratePayslipForm({ employee_id: '', year: '', month: '' });
              }
            }}
          >
            <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', width: '95%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #333' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: 'bold' }}>{t('payslips.generateModal.title', { defaultValue: 'إنشاء كشف راتب' })}</h2>
                <button type="button" onClick={() => { setShowGeneratePayslipModal(false); setGeneratePayslipForm({ employee_id: '', year: '', month: '' }); }} style={{ backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', padding: 0, width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleGeneratePayslipSubmit}>
                {/* Employee select in modal */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ color: 'white', fontWeight: '500', textAlign, margin: 0, direction: dir }}>
                      {t('payslips.generate.employee', { defaultValue: isRTL ? 'الموظف' : 'Employee' })} <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsAddEmployeeModalOpen(true)}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 'bold' }}
                    >
                      <FaPlus style={{ fontSize: '10px' }} />
                      {t('payslips.generate.buttons.addEmployee', { defaultValue: isRTL ? 'إضافة موظف' : 'Add Employee' })}
                    </button>
                  </div>
                  <div ref={modalEmployeeDropdownContainerRef} style={{ position: 'relative' }} data-dropdown-container>
                    <div
                      data-dropdown-toggle
                      onClick={() => setModalEmployeeDropdownOpen((prev) => !prev)}
                      style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: dir, textAlign, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                    >
                      <span>
                        {generatePayslipForm.employee_id
                          ? (() => {
                            const selected = modalEmployeeOptions.find((e) => String(e.id) === String(generatePayslipForm.employee_id));
                            return selected
                              ? `${(isRTL ? selected.name_ar : selected.name_en) || selected.name} - ${selected.employee_code}`
                              : t('payslips.generate.placeholders.selectEmployee', { defaultValue: isRTL ? 'اختر الموظف' : 'Select Employee' });
                          })()
                          : t('payslips.generate.placeholders.selectEmployee', { defaultValue: isRTL ? 'اختر الموظف' : 'Select Employee' })}
                      </span>
                      <span style={{ fontSize: 12 }}>▼</span>
                    </div>
                    {modalEmployeeDropdownOpen && (
                      <DropdownWithSearch
                        isOpen={modalEmployeeDropdownOpen}
                        searchTerm={modalEmployeeSearchTerm}
                        onSearchChange={setModalEmployeeSearchTerm}
                        searchInputRef={modalEmployeeSearchInputRef}
                        items={modalEmployeeOptions}
                        pagination={modalEmployeePagination}
                        currentPage={modalEmployeePage}
                        onPageChange={setModalEmployeePage}
                        onClose={() => setModalEmployeeDropdownOpen(false)}
                        onSelect={(id) => {
                          setGeneratePayslipForm(prev => ({ ...prev, employee_id: String(id) }));
                          setModalEmployeeDropdownOpen(false);
                          setModalEmployeeSearchTerm('');
                          setModalEmployeePage(1);
                        }}
                        label={isRTL ? 'الموظف' : 'Employee'}
                        isRTL={isRTL}
                        t={t}
                        searchPlaceholder={isRTL ? 'ابحث عن موظف...' : 'Search for employee...'}
                      />
                    )}
                  </div>
                </div>

                {/* Period / Year/Month */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', color: 'white', fontWeight: '500', marginBottom: '8px', textAlign, direction: dir }}>
                    {t('payslips.generate.period', { defaultValue: isRTL ? 'الفترة (السنة والشهر)' : 'Period (Year & Month)' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="month"
                    name="year"
                    value={generatePayslipForm.year && generatePayslipForm.month ? `${generatePayslipForm.year}-${generatePayslipForm.month.toString().padStart(2, '0')}` : ''}
                    onChange={handleGeneratePayslipFormChange}
                    required
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: '30px' }}>
                  <button type="submit" disabled={isLoading} style={{ backgroundColor: isLoading ? '#666' : '#AC2000', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                    {isLoading ? t('payslips.generate.buttons.generating', { defaultValue: 'جاري التوليد...' }) : t('payslips.generate.buttons.submit', { defaultValue: 'توليد كشف راتب' })}
                  </button>
                  <button type="button" onClick={() => { setShowGeneratePayslipModal(false); setGeneratePayslipForm({ employee_id: '', year: '', month: '' }); }} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                    {t('payslips.generate.buttons.cancel', { defaultValue: 'إلغاء' })}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Pdf Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && previewData}
        title={isRTL ? 'معاينة PDF' : 'PDF Preview'}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfType(null);
          setPdfPayload(null);
          setPdfChunks([]);
          setPreviewData(null);
        }}
        onPrint={handlePrintFromPreview}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {previewData?.type === 'single' ? (
            renderSinglePdfPage(previewData.data, true)
          ) : (
            previewData?.chunks?.map((chunk, i) => (
              <React.Fragment key={i}>
                {renderPdfPage(chunk, i, previewData.chunks.length, null)}
              </React.Fragment>
            ))
          )}
        </div>
      </PdfPreviewModal>

      {/* Hidden Templates for printing ALL */}
      {pdfType === 'all' && pdfChunks.map((chunk, i) => (
        <div key={i} id={`payslips-pdf-all-${i}`} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          {renderPdfPage(chunk, i, pdfChunks.length, `payslips-pdf-all-${i}`)}
        </div>
      ))}

      {/* Hidden Template for printing SINGLE */}
      {pdfType === 'statement' && pdfPayload && (
        <div id={`employee-payslip-pdf-single-${pdfPayload.id}`} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          {renderSinglePdfPage(pdfPayload, false)}
        </div>
      )}

      {/* Hidden Measurement Table for dynamic PDF pagination */}
      <div
        id="payslips-measurement-container"
        style={{ position: 'absolute', left: '-9999px', top: 0, width: '714px', visibility: 'hidden', pointerEvents: 'none' }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '35%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
              <th style={colStyles.employee}>{t('payslips.table.employee')}</th>
              <th style={colStyles.period}>{t('payslips.table.payPeriod')}</th>
              <th style={colStyles.grossSalary}>{t('payslips.table.grossSalary')}</th>
              <th style={colStyles.netSalary}>{t('payslips.table.netSalary')}</th>
              <th style={colStyles.status}>{t('payslips.table.status')}</th>
            </tr>
          </thead>
          <tbody>
            {allPayslipsForPdf.map((item, idx) => (
              <tr key={item.id}>
                <td style={{ ...colStyles.employee, direction: isRTL ? 'rtl' : 'ltr' }}>
                  {(isRTL ? item.employee?.name_ar : item.employee?.name_en) || item.employee?.name || ''}
                </td>
                <td style={colStyles.period}>{item.pay_period || ''}</td>
                <td style={colStyles.grossSalary}>{item.gross_salary ? parseFloat(item.gross_salary).toLocaleString() : ''}</td>
                <td style={colStyles.netSalary}>{item.net_salary ? parseFloat(item.net_salary).toLocaleString() : ''}</td>
                <td style={colStyles.status}>
                  {item.status === 'generated'
                    ? t('payslips.status.generated')
                    : item.status === 'posted'
                      ? t('payslips.status.posted', { defaultValue: 'مرحل' })
                      : item.status === 'paid'
                        ? t('payslips.status.paid')
                        : item.status || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};


// Employee Dropdown with Search Component
const EmployeeDropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');
  const normalizedDropdownSearch = String(searchTerm || '').trim().toLowerCase();
  const filteredItems = normalizedDropdownSearch
    ? (items || []).filter((item) => {
        const searchable = [
          item.name,
          item.name_ar,
          item.name_en,
          item.employee_code,
          item.code,
        ]
          .filter((value) => value !== undefined && value !== null)
          .join(' ')
          .toLowerCase();
        return searchable.includes(normalizedDropdownSearch);
      })
    : (items || []);
  const shouldShowPagination = pagination
    && filteredItems.length > 0
    && (!normalizedDropdownSearch || filteredItems.length === (items || []).length)
    && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1));

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  const dropdownMenuStyle = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    backgroundColor: '#1a1f2e',
    border: '1px solid #333',
    borderRadius: 8,
    zIndex: 20,
    padding: 12,
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  };

  const dropdownSearchStyle = {
    width: '100%',
    padding: 10,
    borderRadius: 6,
    border: '1px solid #333',
    backgroundColor: '#14263b',
    color: 'white',
    marginBottom: 10,
  };

  const dropdownEmptyStyle = {
    padding: 12,
    textAlign: 'center',
    color: '#ccc',
    fontSize: 13,
  };

  return (
    <div style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t('payslips.generate.placeholders.searchEmployee', { defaultValue: isRTL ? 'ابحث عن موظف...' : 'Search employee...' })}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}
        />
      </div>
      {shouldShowPagination && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
              {t('payslips.generate.pagination.page', { defaultValue: 'صفحة' })} {pagination.current_page} {t('payslips.generate.pagination.of', { defaultValue: 'من' })} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ‹
            </button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPageChange(page); }}
                  style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}
                >
                  {page}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              »»
            </button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {filteredItems.length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
            {t('payslips.generate.pagination.noResults', { defaultValue: isRTL ? 'لا توجد نتائج' : 'No results' })}
          </div>
        ) : (
          filteredItems.map((item) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dropdownIsRTL ? 'rtl' : 'ltr',
                textAlign: dropdownIsRTL ? 'right' : 'left',
                color: 'white'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {(dropdownIsRTL ? item.name_ar : item.name_en) || item.name} {item.employee_code ? `- ${item.employee_code}` : ''}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// DropdownWithSearch component for roles (used in AddEmployeeModal)
const RoleDropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');
  const normalizedDropdownSearch = String(searchTerm || '').trim().toLowerCase();
  const filteredItems = normalizedDropdownSearch
    ? (items || []).filter((item) => {
        const searchable = [
          item.name,
          item.name_ar,
          item.name_en,
          item.display_name,
        ]
          .filter((value) => value !== undefined && value !== null)
          .join(' ')
          .toLowerCase();
        return searchable.includes(normalizedDropdownSearch);
      })
    : (items || []);
  const shouldShowPagination = pagination
    && filteredItems.length > 0
    && (!normalizedDropdownSearch || filteredItems.length === (items || []).length)
    && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1));

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  const dropdownMenuStyle = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    backgroundColor: '#1a1f2e',
    border: '1px solid #333',
    borderRadius: 8,
    zIndex: 20,
    padding: 12,
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  };

  const dropdownSearchStyle = {
    width: '100%',
    padding: 10,
    borderRadius: 6,
    border: '1px solid #333',
    backgroundColor: '#14263b',
    color: 'white',
    marginBottom: 10,
  };

  const dropdownEmptyStyle = {
    padding: 12,
    textAlign: 'center',
    color: '#ccc',
    fontSize: 13,
  };

  return (
    <div style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t('employees.form.placeholders.searchRole', { defaultValue: 'ابحث عن دور...' })}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}
        />
      </div>
      {shouldShowPagination && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
              {t('employees.form.pagination.page', { defaultValue: 'صفحة' })} {pagination.current_page} {t('employees.form.pagination.of', { defaultValue: 'من' })} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ‹
            </button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPageChange(page); }}
                  style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}
                >
                  {page}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              »»
            </button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {filteredItems.length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
            {t('employees.form.pagination.noResults', { defaultValue: 'لا توجد نتائج' })}
          </div>
        ) : (
          filteredItems.map((item) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dropdownIsRTL ? 'rtl' : 'ltr',
                textAlign: dropdownIsRTL ? 'right' : 'left',
                color: 'white'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {item.name}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Add Employee Modal Component (copied from AddLeaveRequest.jsx)
const AddEmployeeModalComponent = ({ isOpen, onClose, onSuccess, t, isRTL, dir, textAlign, dispatch, departments, positions, workShifts, i18n }) => {
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    username: '',
    password: '',
    password_confirmation: '',
    email: '',
    phone: '',
    employee_code: '',
    national_id: '',
    birth_date: '',
    gender: 'male',
    department_id: '',
    position_id: '',
    work_shift_id: '',
    role_id: '',
    is_active: true,
    can_start_tasks_without_approval: false,
  });
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolePage, setRolePage] = useState(1);
  const [rolePagination, setRolePagination] = useState(null);
  const roleSearchInputRef = useRef(null);
  const [usernameStatus, setUsernameStatus] = useState('idle');
  const [usernameMessage, setUsernameMessage] = useState('');
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const usernameCheckTimeoutRef = useRef(null);
  const lastErrorRefModal = useRef({ message: null, time: 0 });
  const { success, error, isLoading } = useSelector((state) => state.auth);

  const normalizeListResponseForModal = (payload) => {
    if (!payload) return { items: [], pagination: null };
    let items = [];
    let pagination = null;
    if (Array.isArray(payload?.data?.data)) {
      items = payload.data.data;
      pagination = payload.data?.meta || payload.data?.pagination || null;
    } else if (Array.isArray(payload?.data)) {
      items = payload.data;
      pagination = payload.meta || payload.pagination || null;
    } else if (Array.isArray(payload?.items)) {
      items = payload.items;
      pagination = payload.pagination || payload.meta || null;
    } else if (Array.isArray(payload)) {
      items = payload;
      pagination = null;
    } else {
      items = [];
      pagination = payload.pagination || payload.meta || null;
    }
    if (pagination) {
      pagination = {
        current_page: pagination.current_page || pagination.page || 1,
        last_page: pagination.last_page || pagination.total_pages || 1,
        per_page: pagination.per_page || 10,
        total: pagination.total || 0,
      };
    }
    return { items, pagination };
  };

  const fetchRoles = useCallback(
    async (page = rolePage, search = roleSearchTerm) => {
      try {
        const payload = await dispatch(
          getRoles({
            page,
            per_page: 10,
            search: search || undefined,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponseForModal(payload);
        setRoleOptions(items);
        setRolePagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('employees.form.messages.fetchRolesError', { defaultValue: 'فشل جلب الأدوار' }), { rtl: isRTL });
      }
    },
    [dispatch, t, isRTL]
  );

  useEffect(() => {
    if (roleDropdownOpen) {
      fetchRoles(rolePage, roleSearchTerm);
    }
  }, [roleDropdownOpen, rolePage, fetchRoles]);

  useEffect(() => {
    if (roleSearchTerm !== undefined && roleDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setRolePage(1);
        fetchRoles(1, roleSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [roleSearchTerm, roleDropdownOpen, fetchRoles]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRefModal.current;
      // Only show toast if it's a different error or it's been more than 2 seconds since last error
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRefModal.current = { message: error, time: now };
      }
      // Clear error after showing toast
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, isRTL, dispatch]);

  const isUsernameAvailable = async (username) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) return false;
      const response = await axios.post(`${BASE_URL}/auth/check-username`, { username }, {
        headers: { Authorization: `Bearer ${token}`, 'Accept-Language': currentLang },
      });
      return !response?.data?.data?.exists;
    } catch { return false; }
  };

  const generateUsernameSuggestions = async (baseUsername) => {
    const suggestions = [];
    let attempt = 1;
    while (suggestions.length < 3 && attempt <= 30) {
      const candidate = `${baseUsername}${attempt}`;
      if (candidate !== baseUsername && !(await isUsernameAvailable(candidate))) {
        attempt += 1;
        continue;
      }
      if (!suggestions.includes(candidate)) suggestions.push(candidate);
      attempt += 1;
    }
    setUsernameSuggestions(suggestions);
  };

  const checkUsernameAvailability = async (username, withSuggestions = false) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        setUsernameStatus('error');
        setUsernameMessage(t('employees.form.validation.requiredFields'));
        return;
      }
      const response = await axios.post(`${BASE_URL}/auth/check-username`, { username }, {
        headers: { Authorization: `Bearer ${token}`, 'Accept-Language': currentLang },
      });
      const exists = response?.data?.data?.exists;
      if (exists) {
        setUsernameStatus('exists');
        setUsernameMessage(t('employees.form.usernameMessages.exists'));
        if (withSuggestions) await generateUsernameSuggestions(username);
      } else {
        setUsernameStatus('available');
        setUsernameMessage(t('employees.form.usernameMessages.available', { username }));
        setUsernameSuggestions([]);
      }
    } catch {
      setUsernameStatus('error');
      setUsernameMessage(t('employees.form.usernameMessages.exists'));
    }
  };

  const handleUsernameSuggestionClick = (suggestion) => {
    setFormData(prev => ({ ...prev, username: suggestion }));
    setUsernameSuggestions([]);
    checkUsernameAvailability(suggestion, false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'can_start_tasks_without_approval') {
      setFormData(prev => ({ ...prev, [name]: value === '1' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'username') {
      const username = value.trim();
      if (usernameCheckTimeoutRef.current) clearTimeout(usernameCheckTimeoutRef.current);
      if (!username) {
        setUsernameStatus('idle');
        setUsernameMessage('');
        setUsernameSuggestions([]);
        return;
      }
      setUsernameStatus('checking');
      setUsernameMessage(t('employees.form.usernameMessages.checking'));
      setUsernameSuggestions([]);
      usernameCheckTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(username, true);
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en || !formData.name_ar || !formData.phone || !formData.birth_date) {
      const errorMsg = t('employees.form.validation.requiredFields');
      const now = Date.now();
      const last = lastErrorRefModal.current;
      if (!last.message || last.message !== errorMsg || now - last.time > 2000) {
        toast.error(errorMsg, { rtl: isRTL });
        lastErrorRefModal.current = { message: errorMsg, time: now };
      }
      return;
    }

    const trimmedUsername = (formData.username || '').trim();
    const hasUsername = trimmedUsername.length > 0;

    if (hasUsername) {
      if (!formData.password || !formData.password_confirmation) {
        const errorMsg = t('employees.form.validation.passwordRequired', { defaultValue: 'Password and password confirmation are required when entering a username' });
        const now = Date.now();
        const last = lastErrorRefModal.current;
        if (!last.message || last.message !== errorMsg || now - last.time > 2000) {
          toast.error(errorMsg, { rtl: isRTL });
          lastErrorRefModal.current = { message: errorMsg, time: now };
        }
        return;
      }
      if (formData.password !== formData.password_confirmation) {
        const errorMsg = t('employees.form.validation.passwordMismatch', { defaultValue: 'Password and password confirmation do not match' });
        const now = Date.now();
        const last = lastErrorRefModal.current;
        if (!last.message || last.message !== errorMsg || now - last.time > 2000) {
          toast.error(errorMsg, { rtl: isRTL });
          lastErrorRefModal.current = { message: errorMsg, time: now };
        }
        return;
      }
      if (usernameStatus === 'exists') {
        const errorMsg = t('employees.form.usernameMessages.exists');
        const now = Date.now();
        const last = lastErrorRefModal.current;
        if (!last.message || last.message !== errorMsg || now - last.time > 2000) {
          toast.error(errorMsg, { rtl: isRTL });
          lastErrorRefModal.current = { message: errorMsg, time: now };
        }
        return;
      }
      const available = await isUsernameAvailable(trimmedUsername);
      if (!available) {
        setUsernameStatus('exists');
        setUsernameMessage(t('employees.form.usernameMessages.exists'));
        await generateUsernameSuggestions(trimmedUsername);
        const errorMsg = t('employees.form.usernameMessages.exists');
        const now = Date.now();
        const last = lastErrorRefModal.current;
        if (!last.message || last.message !== errorMsg || now - last.time > 2000) {
          toast.error(errorMsg, { rtl: isRTL });
          lastErrorRefModal.current = { message: errorMsg, time: now };
        }
        return;
      }
    }

    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (!hasUsername && (key === 'password' || key === 'password_confirmation' || key === 'username')) return;
      if (formData[key] !== null && formData[key] !== undefined) {
        if (key === 'is_active' || key === 'can_start_tasks_without_approval') {
          formDataToSend.append(key, formData[key] ? '1' : '0');
        } else if (key === 'department_id' || key === 'position_id' || key === 'work_shift_id' || key === 'role_id') {
          formDataToSend.append(key, formData[key] || '');
        } else if (key === 'email') {
          formDataToSend.append(key, formData[key].trim() === '' ? 'null' : formData[key].trim());
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    formDataToSend.set('can_start_tasks_without_approval', formData.can_start_tasks_without_approval ? '1' : '0');
    if (formData.email === '' || (formData.email && formData.email.trim() === '')) {
      formDataToSend.set('email', 'null');
    }
    if (hasUsername) {
      formDataToSend.set('username', trimmedUsername);
    }

    try {
      const result = await dispatch(addEmployee(formDataToSend)).unwrap();
      if (result?.data?.id) {
        onSuccess(String(result.data.id));
        dispatch(getEmployees({ page: 1, per_page: 10, search: undefined }));
        setFormData({
          name_en: '', name_ar: '', username: '', password: '', password_confirmation: '',
          email: '', phone: '', employee_code: '', national_id: '', birth_date: '',
          gender: 'male', department_id: '', position_id: '', work_shift_id: '', role_id: '',
          is_active: true, can_start_tasks_without_approval: false,
        });
        setUsernameStatus('idle');
        setUsernameMessage('');
        setUsernameSuggestions([]);
        setTimeout(() => { onClose(); }, 500);
      }
    } catch (error) {
      // Error handled by Redux
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#1a1f2e', borderRadius: '12px', padding: '30px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto', position: 'relative', direction: dir }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: 'absolute', top: '1rem', [isRTL ? 'left' : 'right']: '1rem', background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '24px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        <h2 style={{ marginTop: 0, marginBottom: 20, color: 'white', textAlign }}>{t('leaveRequests.add.modals.addEmployee.title')}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
            <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.nameAr')}</label><input type="text" name="name_ar" value={formData.name_ar} onChange={handleInputChange} placeholder={t('employees.form.placeholders.nameAr')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', direction: 'rtl', textAlign: 'right' }} /></div>
            <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.nameEn')}</label><input type="text" name="name_en" value={formData.name_en} onChange={handleInputChange} placeholder={t('employees.form.placeholders.nameEn')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', direction: 'ltr', textAlign: 'left' }} /></div>
            <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.employeeCode')}</label><input type="text" name="employee_code" value={formData.employee_code} onChange={handleInputChange} placeholder={t('employees.form.placeholders.employeeCode')} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} /></div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.username')} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span></label>
              <input type="text" name="username" value={formData.username} onChange={handleInputChange} placeholder={t('employees.form.placeholders.username')} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
              {usernameMessage && <div style={{ marginTop: '6px', fontSize: '12px', color: usernameStatus === 'available' ? '#10b981' : usernameStatus === 'checking' ? '#eab308' : '#f97373' }}>{usernameMessage}</div>}
              {usernameSuggestions.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ marginBottom: '4px', fontSize: '12px', color: '#9ca3af' }}>{t('employees.form.usernameMessages.suggestionsTitle')}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {usernameSuggestions.map((suggestion) => (
                      <button key={suggestion} type="button" onClick={() => handleUsernameSuggestionClick(suggestion)} style={{ padding: '6px 10px', backgroundColor: '#374151', borderRadius: '999px', border: 'none', color: 'white', fontSize: '12px', cursor: 'pointer' }}>{suggestion}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {formData.username.trim() && (
              <>
                <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.password', { defaultValue: 'Password' })}</label><input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder={t('employees.form.placeholders.password', { defaultValue: 'Enter password' })} required={formData.username.trim().length > 0} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} /></div>
                <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.passwordConfirmation', { defaultValue: 'Confirm Password' })}</label><input type="password" name="password_confirmation" value={formData.password_confirmation} onChange={handleInputChange} placeholder={t('employees.form.placeholders.passwordConfirmation', { defaultValue: 'Re-enter password' })} required={formData.username.trim().length > 0} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} /></div>
              </>
            )}
            <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.nationalId')}</label><input type="text" name="national_id" value={formData.national_id} onChange={handleInputChange} placeholder={t('employees.form.placeholders.nationalId')} maxLength={14} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} /></div>
            <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.email')} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span></label><input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder={t('employees.form.placeholders.email')} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} /></div>
            <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.phone')}</label><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t('employees.form.placeholders.phone')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} /></div>
            <div><label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.birthDate')}</label><input type="date" name="birth_date" value={formData.birth_date} onChange={handleInputChange} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} /></div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.gender')}</label>
              <select name="gender" value={formData.gender} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.genderPlaceholder')}</option>
                <option value="male" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.gender.male')}</option>
                <option value="female" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.gender.female')}</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.department')}</label>
              <select name="department_id" value={formData.department_id} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.departmentPlaceholder')}</option>
                {departments?.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.position')}</label>
              <select name="position_id" value={formData.position_id} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.positionPlaceholder')}</option>
                {positions?.map(pos => <option key={pos.id} value={pos.id}>{pos.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.workPlace')}</label>
              <select name="work_shift_id" value={formData.work_shift_id} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.placeholders.workPlace')}</option>
                {workShifts?.map(shift => <option key={shift.id} value={shift.id}>{shift.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.role', { defaultValue: 'الدور' })} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span></label>
              <div style={{ position: 'relative' }} data-dropdown-container>
                <div data-dropdown-toggle onClick={() => setRoleDropdownOpen((prev) => !prev)} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', fontSize: '14px', direction: dir, textAlign, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                  <span>{formData.role_id ? (roleOptions.find((r) => String(r.id) === String(formData.role_id))?.name || t('employees.form.placeholders.selectRole', { defaultValue: 'اختر الدور' })) : t('employees.form.placeholders.selectRole', { defaultValue: 'اختر الدور' })}</span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <RoleDropdownWithSearch isOpen={roleDropdownOpen} searchTerm={roleSearchTerm} onSearchChange={setRoleSearchTerm} searchInputRef={roleSearchInputRef} items={roleOptions} pagination={rolePagination} onPageChange={setRolePage} onClose={() => setRoleDropdownOpen(false)} onSelect={(item) => { setFormData(prev => ({ ...prev, role_id: String(item.id) })); setRoleDropdownOpen(false); setRoleSearchTerm(''); setRolePage(1); }} label={t('employees.form.fields.role')} isRTL={isRTL} i18n={i18n} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.status')}</label>
              <select name="is_active" value={formData.is_active ? 'active' : 'inactive'} onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'active' }))} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="active" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.form.statusOptions.active')}</option>
                <option value="inactive" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.form.statusOptions.inactive')}</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.canStartTasks', { defaultValue: 'Can start tasks without approval' })}</label>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white', cursor: 'pointer' }}>
                  <input type="radio" name="can_start_tasks_without_approval" value="1" checked={formData.can_start_tasks_without_approval === true} onChange={handleInputChange} style={{ cursor: 'pointer' }} />
                  {t('employees.common.yes', { defaultValue: 'Yes' })}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white', cursor: 'pointer' }}>
                  <input type="radio" name="can_start_tasks_without_approval" value="0" checked={formData.can_start_tasks_without_approval === false} onChange={handleInputChange} style={{ cursor: 'pointer' }} />
                  {t('employees.common.no', { defaultValue: 'No' })}
                </label>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'center' }}>
            <button type="submit" disabled={isLoading} style={{ backgroundColor: isLoading ? '#666' : '#AC2000', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: isLoading ? 'not-allowed' : 'pointer' }}>{t('employees.form.buttons.submitAdd')}</button>
            <button type="button" onClick={onClose} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer' }}>{t('employees.form.buttons.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Payslips;

